import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import AIChat from '../components/AIChat';
import Requirements from '../components/Requirements';
import { kybService } from '../api/kybService';
import { VerificationData, Message, Requirement } from '../types';
import { User, VerificationProgress } from '../types/kyb';
import { useParams } from 'react-router-dom';
import { getAuthToken } from '../utils/auth';

const Verification: React.FC = () => {
  // State for verification data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  
  // Default to mockup data in dev mode or while waiting for API
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [conversationHistory, setConversationHistory] = useState<Message[]>([]);
  
  // Get route params in case token is part of URL
  const params = useParams();
  
  // Fetch verification data on component mount
  useEffect(() => {
    const fetchVerificationData = async () => {
      try {
        setLoading(true);
        
        // Get token from the URL or from localStorage
        const token = getAuthToken();
        if (!token) {
          setError('No authentication token found. Please use a valid verification link.');
          return;
        }
        
        // Import default data dynamically to prevent import errors in dev
        const { mockupData } = await import('../mockup-data');
        
        // Set default placeholder data while loading
        setRequirements(mockupData.requirements);
        setConversationHistory(mockupData.conversation);
        
        // Make the API request with the JWT token
        try {
          console.log('Making API request with token:', token.substring(0, 10) + '...');
          const data = await kybService.getCurrentVerification();
          
          if (!data) {
            setError('No verification data returned from the API.');
            return;
          }
          
          // Update state with fetched data
          console.log('Verification data received:', data);
          setVerificationData(data);
          
          // Update requirements and chat if they exist in the API response
          if (data.requirements) setRequirements(data.requirements);
          if (data.conversation) setConversationHistory(data.conversation);
        } catch (apiErr: any) {
          console.error('API request failed:', apiErr);
          if (apiErr?.message?.includes('401')) {
            setError('Authentication failed. Your verification token may be expired or invalid.');
          } else {
            setError(`Failed to fetch verification data: ${apiErr?.message || 'Unknown error'}`);
          }
        }
      } catch (err) {
        console.error('Error in verification component:', err);
        setError('An unexpected error occurred while loading verification data.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVerificationData();
  }, []);
  
  // Track new messages to potentially add requirements based on conversation
  const handleMessageSent = (message: string) => {
    // In a real app, this would be handled by the backend
    console.log('Message sent:', message);
  };

  // Handle suggestion clicks
  const handleSuggestionSelected = (suggestion: string) => {
    // In a real app, this would be handled by the backend
    console.log('Suggestion selected:', suggestion);
  };

  // Simulating requirement triggers based on AI responses
  const handleRequirementTriggered = (triggerMessage: string) => {
    // In a real app, the backend would determine what requirements to add
    
    // Here we're simulating a simple requirement addition based on specific trigger phrases
    if (triggerMessage.toLowerCase().includes('beneficial owner')) {
      const newRequirement: Requirement = {
        id: `new-${Date.now()}`,
        category: 'Ownership Structure',
        title: 'New Beneficial Owner Documentation',
        description: 'Proof of identity for newly added beneficial owner',
        status: 'pending' as const,
        dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      };
      
      setRequirements(prevReqs => [...prevReqs, newRequirement]);
    }
  };

  // Handle requirement status changes
  const handleRequirementStatusChange = (requirementId: number | string, newStatus: string) => {
    console.log(`Requirement ${requirementId} status changed to ${newStatus}`);
    // In a real app, this would update the backend
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-50">
        <div className="text-center p-8">
          <div className="w-16 h-16 border-4 border-t-primary-500 border-primary-100 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-neutral-800">Loading verification data...</h2>
          <p className="text-neutral-500 mt-2">Please wait while we retrieve your information</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-50">
        <div className="text-center p-8 max-w-lg bg-white rounded-xl shadow-card border border-neutral-200">
          <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-800 mb-2">Verification Error</h2>
          <p className="text-neutral-600 mb-6">{error}</p>
          <a href="/" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
            Return to Home
          </a>
        </div>
      </div>
    );
  }
  
  // If verification data is missing, we'll display an error
  if (!verificationData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-50">
        <div className="text-center p-8 max-w-lg bg-white rounded-xl shadow-card border border-neutral-200">
          <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-800 mb-2">Verification Data Missing</h2>
          <p className="text-neutral-600 mb-6">Unable to load verification data. Please try again with a valid token.</p>
          <a href="/" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
            Return to Home
          </a>
        </div>
      </div>
    );
  }
  
  // Data is available, extract what we need and ensure types are compatible
  const userData: User = {
    name: verificationData.user.name,
    email: verificationData.user.email,
    role: verificationData.user.role || 'User', // Provide default if missing
    avatar: verificationData.user.avatar || '',
    notifications: verificationData.user.notifications || 0
  };
  
  const progressData: VerificationProgress = verificationData.progress;
  const companyData = verificationData.company;

  // Create Requirements dashboard as right sidebar
  const requirementsDashboard = (
    <Requirements
      initialRequirements={requirements}
      onRequirementStatusChange={handleRequirementStatusChange}
    />
  );

  return (
    <Layout
      title="Business Verification"
      user={userData}
      progress={progressData}
      companyName={companyData.name}
      registrationNumber={companyData.registrationNumber}
      taxId={companyData.taxId || ''}
      rightSidebar={requirementsDashboard}
    >
      <AIChat 
        initialMessages={conversationHistory}
        onMessageSent={handleMessageSent}
        onSuggestionSelected={handleSuggestionSelected}
        onRequirementTriggered={handleRequirementTriggered}
      />
    </Layout>
  );
};

export default Verification;